/**
 * REVIEW COVERAGE — the partition, the restated miss, and the hold.
 * PURE. Zero imports, so verify-reviews.mjs drives this exact code under Node.
 *
 * Brian: "i want to know that every one of our clients is being treated as such
 * to ask for a 5 star review... also understanding who and what to ask is
 * extremely important."
 *
 * Three jobs, in the order they matter.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 1. THE PARTITION — and the defect it exists to kill.
 *
 * `reviews.js#/queue` computes EIGHT mutually exclusive states that sum to the
 * scoped book, plus TWO overlays (`suppressed`, `allAsked`) that are counted on
 * top of whichever state the order is already in. Its own comment says so:
 * "not extra states, and are labelled that way so nobody adds the column up."
 *
 * The screen then printed all of them in one flat `.tally` row at one weight —
 * and DROPPED `notDelivered` entirely. So the row a rep read was:
 *
 *     121 at peak · 58 warm · 31 cooling · 44 too early · 12 in transit ·
 *     9 delivered with no date · 803 fully asked · 4 do-not-ask
 *
 * Eight numbers that look exactly like a partition, that cannot be added up
 * (two are overlays), and that omit the single biggest answer to "why isn't my
 * client on this screen" — `notDelivered`, which on the live board is 958 of
 * 3,467 orders.
 *
 * This is the same defect as `stageTally` in lib/championships.js, and it cost
 * more there than a wrong renderer would have: an auditor read a summary bar
 * that silently omitted three stages, concluded 203 ROWS were unreachable, and
 * ranked a non-existent bug first. A summary that omits a bucket does not just
 * under-report — it teaches the reader that records are missing.
 *
 * So `coverageTally` walks EVERY declared state, reports what it could not
 * place as `stray` rather than dropping it, and exposes `reconciled` as a
 * checkable property instead of a sentence somebody typed once.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 2. THE RESTATED MISS — the finding that changes behaviour.
 *
 * The headline read "2,178 asks never made". Measured against the live order
 * board on 2026-09-11 (3,467 orders), that number is mostly an artefact:
 *
 *   · Of 1,905 orders in `complete` with a date_closed, exactly TWO closed
 *     inside the 45-day ask window. Median age 229 days, max 1,254.
 *   · The closes STACK: 123 orders share one day, 147 another, 89 another.
 *     989 of 1,905 land in 2026-01 alone, across just 21 distinct months.
 *   · The control group proves it. The 560 orders in `won - delivered…` carry
 *     NO date_closed, fall back to `date_updated`, and 503 of those 560 sit
 *     INSIDE the window.
 *
 * A company shipping continuously does not deliver 123 orders on one day and
 * then nothing for six weeks. Those are BULK STATUS EDITS, and `date_closed`
 * records the day somebody mass-moved the records — not the day a customer
 * opened a box. `review-timing.js#deliveredOn` is already honest about this: it
 * marks such a date `status_inferred` and `firm: false`, and says in its own
 * comment that "a queue that quietly treats a guess as a measurement will
 * eventually tell a rep to ask on a day that never happened."
 *
 * Nobody had read that flag back out. So `splitMissed` does:
 *
 *   missed + deliveredFirm     → NOBODY ASKED. A real In Hands Date, a real
 *                                window, and it went by. This is the number.
 *   missed + NOT deliveredFirm → SCORED OFF A DATE THAT IS NOT A DELIVERY.
 *                                Not anyone's failure, and never counted as one.
 *
 * ⚠️ THE RULE IS THE STRUCTURAL FLAG, NOT THE CLUSTERING. `dayClusters` exists
 * to SHOW the reader the stacking that justifies the split, and is deliberately
 * not what decides it — a genuinely busy delivery day would be indistinguishable
 * from a bulk edit by count alone. Evidence on screen, structure in the rule.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 3. HOLD — and what it is emphatically NOT.
 *
 * Brian, ruling out the risk flag this file was originally going to carry:
 * "No repairs don't necessarily mean an unhappy customer, just don't want that
 * used."
 *
 * He is right, and it is the same error this repo has caught twice before. The
 * JD Warranty Remakes list holds 383 tickets; roughly half are a RESIZE. A
 * customer whose ring was resized and now fits is a happy customer, and
 * flagging them would have silenced a real five-star review. Same for the
 * `Revision Required` checkbox (design-phase iteration, not unhappiness) and
 * the 385 "Add On / Add Order" orders (more business, not a complaint).
 *
 * SO, STATED PLAINLY AND ASSERTED IN THE TEST SUITE: there is NO reliable
 * customer-unhappiness signal in SR's systems today, and nothing in this file
 * infers one. If one is ever wanted it has to be CAPTURED deliberately — a
 * field somebody fills in — not derived from operational exhaust.
 *
 * HOLD therefore means exactly one thing: **we cannot confidently address this
 * ask.** No email, no named human, or a delivery type that makes the recipient
 * unknowable. It is a machine saying "I don't know", and it is kept structurally
 * apart from `suppressed`, which is a HUMAN'S deliberate decision carrying a
 * reason and a name. A machine's uncertainty must never wear a human's
 * authority — so they are different fields, different words, and `holdFor`
 * cannot produce a suppression.
 */

/* ── the three coverage groups ───────────────────────────────────────── */

export const TO_ASK = 'to-ask'
export const NOT_ELIGIBLE = 'not-eligible'
export const WINDOW_CLOSED = 'window-closed'

export const COVERAGE_GROUPS = [
  {
    key: TO_ASK,
    label: 'To ask',
    blurb: 'Delivered, inside the window, still has something to ask for.',
  },
  {
    key: NOT_ELIGIBLE,
    label: 'Not yet eligible',
    blurb: 'Nothing to ask about yet. These arrive on their own — asking sooner is the thing the queue exists to prevent.',
  },
  {
    key: WINDOW_CLOSED,
    label: 'Window closed',
    blurb: 'Past 45 days from delivery. Broken down below, because most of it is not what it looks like.',
  },
]

/**
 * THE EIGHT EXCLUSIVE STATES. Keys are `reviews.js`'s own tally keys, so this
 * list and the backend cannot drift without `stray` reporting it.
 */
export const COVERAGE_STATES = [
  { key: 'peak', label: 'At peak', group: TO_ASK, why: 'The moment. Ask today.' },
  { key: 'warm', label: 'Still warm', group: TO_ASK, why: 'Worth asking today.' },
  { key: 'closing', label: 'Cooling', group: TO_ASK, why: 'Ask now or let it go.' },

  { key: 'notDelivered', label: 'Not delivered', group: NOT_ELIGIBLE, why: 'They have not experienced anything yet. The largest group, and the answer to most "why is my client not here" questions.' },
  { key: 'inTransit', label: 'In transit', group: NOT_ELIGIBLE, why: 'Left SR, nobody is holding them. Asking now is asking about a box in a van.' },
  { key: 'tooEarly', label: 'Too early', group: NOT_ELIGIBLE, why: 'Delivered, still settling. Joins the queue on its own.' },
  { key: 'noDate', label: 'Delivered, no date', group: NOT_ELIGIBLE, why: 'Status says delivered but no In Hands Date, so nobody can tell if this was yesterday or last spring. Fill the date in and it joins the queue.' },

  { key: 'missed', label: 'Past the window', group: WINDOW_CLOSED, why: 'Delivered more than 45 days ago.' },
]

/**
 * OVERLAYS. Counted ON TOP of a state, never beside it. Kept in their own list
 * so a caller physically cannot fold them into the partition — the previous
 * screen put them in the same flat row, which is how they came to be read as
 * buckets.
 */
export const COVERAGE_OVERLAYS = [
  { key: 'allAsked', label: 'Fully asked', why: 'Every available type already asked for. Still counted inside its own state above — an overlay, not a ninth bucket.' },
  { key: 'suppressed', label: 'Do not ask', why: 'A person decided this account should not be asked, and left a reason. Also an overlay.' },
]

const STATE_BY_KEY = Object.fromEntries(COVERAGE_STATES.map((s) => [s.key, s]))
const OVERLAY_BY_KEY = Object.fromEntries(COVERAGE_OVERLAYS.map((o) => [o.key, o]))

export const isOverlayKey = (k) => Object.prototype.hasOwnProperty.call(OVERLAY_BY_KEY, k)
export const isStateKey = (k) => Object.prototype.hasOwnProperty.call(STATE_BY_KEY, k)

const num = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0)

/**
 * The arithmetic, derived — same contract as `stageTally`.
 *
 * `total` is the universe the caller believes it is describing. Pass it and the
 * reconciliation is a real check against an independent number; omit it and it
 * degenerates to "these rows sum to themselves", which is true by construction
 * and proves nothing. The panel always passes it.
 */
export function coverageTally(tally, total = null) {
  const t = tally && typeof tally === 'object' ? tally : {}

  const rows = COVERAGE_STATES.map((s) => ({ ...s, count: num(t[s.key]) }))
  const accounted = rows.reduce((n, r) => n + r.count, 0)

  const groups = COVERAGE_GROUPS.map((g) => ({
    ...g,
    count: rows.filter((r) => r.group === g.key).reduce((n, r) => n + r.count, 0),
    states: rows.filter((r) => r.group === g.key),
  }))

  const overlays = COVERAGE_OVERLAYS.map((o) => ({ ...o, count: num(t[o.key]) }))

  /* A key the backend sent that this file does not know about. Structurally
   * unreachable while both lists agree — which is exactly why it is reported
   * rather than ignored: a ninth state added backend-side shows up here on the
   * first render instead of silently vanishing from the partition. */
  const stray = Object.keys(t)
    .filter((k) => !isStateKey(k) && !isOverlayKey(k))
    .map((k) => ({ key: k, count: num(t[k]) }))
    .filter((r) => r.count > 0)

  const expected = total == null ? accounted : total
  const missing = expected - accounted

  /* AN OVERLAY LARGER THAN THE PARTITION IS DEFINITIONALLY BROKEN, and this is
   * the one arithmetic check that is genuinely independent. `allAsked` and
   * `suppressed` are counted on top of the eight states, so each must be a
   * SUBSET of them — if `suppressed` ever exceeds `accounted` the two numbers
   * are not describing the same universe and neither can be trusted.
   *
   * It matters because `total` is usually null here: the queue route returns no
   * separate universe size, so `accounted` is all we have and comparing it to
   * itself would prove nothing. `stray` and this check are the two that do real
   * work, and the panel's footer states which property it actually verified
   * rather than implying a sum it never performed. */
  const overlayProblems = overlays
    .filter((o) => o.count > accounted)
    .map((o) => ({ key: o.key, label: o.label, count: o.count }))

  return {
    rows,
    groups,
    overlays,
    accounted,
    total: expected,
    stray,
    missing,
    overlayProblems,
    // True when `total` was supplied and genuinely checked, so the footer can
    // say which claim it is making instead of overstating it.
    totalChecked: total != null,
    // The property the footer asserts, rather than a sentence it prints.
    reconciled: missing === 0 && stray.length === 0 && overlayProblems.length === 0,
  }
}

/* ── the restated miss ───────────────────────────────────────────────── */

/**
 * Did this row's delivery date come from a real In Hands Date?
 *
 * `deliveredFirm` is the backend's own flag and is the rule. `deliveredSource`
 * is read only as a fallback for a row that predates the flag — and the
 * fallback is CONSERVATIVE: anything that is not explicitly `in_hands_date`
 * counts as not-firm. Guessing "firm" would move a record INTO "nobody asked"
 * and put a failure on a rep's name that the data cannot support.
 */
export function isFirmDelivery(row) {
  const r = row?.readiness || row || {}
  if (r.deliveredFirm === true) return true
  if (r.deliveredFirm === false) return false
  return r.deliveredSource === 'in_hands_date'
}

export const MISSED_UNASKED = 'unasked'
export const MISSED_NO_REAL_DATE = 'no-real-date'

export const MISSED_KINDS = {
  [MISSED_UNASKED]: {
    key: MISSED_UNASKED,
    label: 'Nobody asked',
    short: 'asks genuinely missed',
    why: 'A real In Hands Date, a real 45-day window, and it went by with no ask. This is the number that means what it says.',
  },
  [MISSED_NO_REAL_DATE]: {
    key: MISSED_NO_REAL_DATE,
    label: 'Scored off a date that is not a delivery',
    short: 'no real delivery date',
    why: 'No In Hands Date on the order, so the age was inferred from when the record was last touched. On this board those timestamps stack — hundreds of orders share a single day — which is bulk status editing, not delivery. Not a missed ask, and not anybody’s failure.',
  },
}

export function missedKindOf(row) {
  return isFirmDelivery(row) ? MISSED_UNASKED : MISSED_NO_REAL_DATE
}

/**
 * Split the missed rows. `rows` are what `GET /api/reviews/queue?state=missed`
 * returns — the same row shape the queue already renders, so no new contract.
 */
export function splitMissed(rows) {
  const all = Array.isArray(rows) ? rows : []
  const unasked = all.filter((r) => missedKindOf(r) === MISSED_UNASKED)
  const noRealDate = all.filter((r) => missedKindOf(r) === MISSED_NO_REAL_DATE)
  return {
    unasked,
    noRealDate,
    total: all.length,
    counts: { [MISSED_UNASKED]: unasked.length, [MISSED_NO_REAL_DATE]: noRealDate.length },
    // Same discipline as everywhere else: the two halves must equal the whole.
    reconciled: unasked.length + noRealDate.length === all.length,
  }
}

/** A day holding at least this many "deliveries" is evidence, not a delivery day. */
export const CLUSTER_MIN = 20

/**
 * The receipt for the split — NOT the rule.
 *
 * Groups the rows by the calendar day their delivery date lands on and returns
 * the days carrying `CLUSTER_MIN` or more. Rendering this is what lets a reader
 * check the claim instead of taking it on trust.
 *
 * ⚠️ LOCAL PARTS, NEVER toISOString(). `toISOString().slice(0,10)` converts to
 * UTC first, so west of Greenwich an evening timestamp keys to the NEXT day and
 * a single bulk edit splits across two buckets — under-reporting the very
 * stacking this function exists to show. Exactly the bug fixed in the Command
 * Center's `dayKey()`.
 */
export function dayKey(value) {
  if (value == null || value === '') return null
  const d = new Date(value)
  const t = d.getTime()
  if (!Number.isFinite(t)) return null
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function dayClusters(rows, min = CLUSTER_MIN) {
  const counts = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const k = dayKey(r?.readiness?.deliveredAt)
    if (!k) continue
    counts.set(k, (counts.get(k) || 0) + 1)
  }
  const clusters = [...counts.entries()]
    .map(([day, count]) => ({ day, count }))
    .filter((c) => c.count >= min)
    .sort((a, b) => b.count - a.count || (a.day < b.day ? -1 : 1))
  return {
    clusters,
    days: counts.size,
    clustered: clusters.reduce((n, c) => n + c.count, 0),
  }
}

/* ── hold: "we cannot confidently address this", nothing more ─────────── */

export const HOLD_NO_EMAIL = 'no-email'
export const HOLD_NO_CONTACT = 'no-contact'
export const HOLD_UNKNOWN_RECIPIENT = 'unknown-recipient'

export const HOLD_REASONS = {
  [HOLD_NO_EMAIL]: {
    key: HOLD_NO_EMAIL,
    label: 'No email on the order',
    fix: 'Add the contact’s email to the ClickUp order and it returns to the queue.',
  },
  [HOLD_NO_CONTACT]: {
    key: HOLD_NO_CONTACT,
    label: 'No named contact',
    fix: 'Nobody is recorded as the Contact or Purchasing POC, so there is no person to address.',
  },
  [HOLD_UNKNOWN_RECIPIENT]: {
    key: HOLD_UNKNOWN_RECIPIENT,
    label: 'Recipient not knowable',
    fix: 'No delivery type is recorded, so whether one person or forty are holding these is unknown. Confirm who took delivery.',
  },
}

const str = (v) => (typeof v === 'string' ? v.trim() : '')

/**
 * Why this row cannot be confidently addressed. Returns every reason, because
 * a row missing both an email and a name needs both fixed and reporting one
 * sends somebody back twice.
 *
 * This is a MACHINE judgement about ADDRESSABILITY. It says nothing about the
 * customer, and `verify-reviews.mjs` asserts that none of its wording ever
 * implies it does.
 */
export function holdFor(row) {
  const reasons = []
  if (!str(row?.contactEmail)) reasons.push(HOLD_REASONS[HOLD_NO_EMAIL])
  if (!str(row?.contact)) reasons.push(HOLD_REASONS[HOLD_NO_CONTACT])
  if (row?.readiness?.unknownDeliveryType) reasons.push(HOLD_REASONS[HOLD_UNKNOWN_RECIPIENT])
  return {
    held: reasons.length > 0,
    reasons,
    // Never a suppression. A hold is reversible by filling in a field; a
    // suppression is a decision a person made and owns.
    kind: 'hold',
  }
}

/**
 * Split an askable list into what can be sent now and what is waiting on a
 * field. Nothing is removed from the universe — `held` is rendered, not hidden,
 * because a held row IS the work: it is one missing email away from an ask.
 */
export function splitHold(rows) {
  const all = Array.isArray(rows) ? rows : []
  const held = []
  const ready = []
  for (const r of all) (holdFor(r).held ? held : ready).push(r)
  return {
    ready,
    held,
    total: all.length,
    reconciled: ready.length + held.length === all.length,
  }
}

/** Why a held row is held, deduped and ready to print on one line. */
export function holdSummary(rows) {
  const counts = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    for (const reason of holdFor(r).reasons) {
      counts.set(reason.key, (counts.get(reason.key) || 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([key, count]) => ({ ...HOLD_REASONS[key], count }))
    .sort((a, b) => b.count - a.count)
}

/**
 * THE STANDING STATEMENT, rendered verbatim on the panel.
 *
 * It lives here rather than in the component for the same reason
 * `STUDIO_RELATIONSHIP` does in lib/aiTools.js: the same claim told in two
 * places drifts, and this is a claim about what SR does NOT know. Softening it
 * into "we're working on sentiment" would be the beginning of somebody
 * inferring it again.
 */
export const NO_RISK_SIGNAL =
  'SR records no measure of whether a customer is happy. There is no complaint field, ' +
  'no ticket, no satisfaction score, and delivery lateness cannot be derived because the ' +
  'In Hands Date is used as both the promised date and the actual one. Repairs and remakes ' +
  'are deliberately NOT read as unhappiness — about half are a resize, and a resized ring ' +
  'that now fits is a happy customer. Nothing here guesses. If we want to know, somebody has ' +
  'to record it.'
